import React,{useEffect,useState} from "react";
import {useLocation,useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import axiosInstance from "../axiosInstance";
import {
 deleteNotification,
 getNotifications,
 markAllNotificationsRead,
 markNotificationRead,
} from "./communicationService";
import "./Communications.css";
import "./WhatsAppHandover.css";
import WhatsAppServiceRequest from "./WhatsAppServiceRequest";

function icon(category){
 return category==="PAYMENT"?"bi-receipt":
  category==="CLAIM"?"bi-heart-pulse":
  category==="PROPOSAL"?"bi-file-earmark-text":
  category==="COMMISSION"?"bi-cash-coin":
  category==="POLICY"?"bi-shield-check":
  category==="BILLING"?"bi-calendar2-check":
  "bi-bell";
}

export default function NotificationInbox(){
 const [data,setData]=useState({notifications:[],unread_count:0});
 const [loading,setLoading]=useState(true);
 const [selected,setSelected]=useState(null);
 const [handover,setHandover]=useState(null);
 const [handoverLoading,setHandoverLoading]=useState(false);
 const [handoverError,setHandoverError]=useState("");
 const [resolutionNote,setResolutionNote]=useState("");
 const [actionBusy,setActionBusy]=useState(false);
 const navigate=useNavigate();
 const location=useLocation();
 const handoverId=new URLSearchParams(location.search).get("handover");
 const serviceRequestId=new URLSearchParams(location.search).get("service_request");

 const load=()=>getNotifications()
  .then(setData)
  .finally(()=>setLoading(false));

 useEffect(()=>{
  let active=true;

  getNotifications()
   .then(result=>{
    if(!active)return;

    setData(result);
    const requestedId=location.state?.notificationId;
    if(requestedId){
     const requested=(result.notifications||[]).find(
      item=>item.id===requestedId
     );
     if(requested){
      setSelected({...requested,is_read:true});
     }
     // Remove transient navigation state so refresh does not reopen it.
     navigate("/notifications",{replace:true,state:null});
    }
   })
   .catch(error=>{
    console.error("Could not load notifications.",error);
   })
   .finally(()=>{
    if(active)setLoading(false);
   });

  return()=>{active=false};
 },[location.state?.notificationId,navigate]);

 useEffect(()=>{
  let active=true;
  if(!handoverId){
   setHandover(null);
   setHandoverError("");
   return()=>{active=false};
  }
  setHandoverLoading(true);
  setHandoverError("");
  axiosInstance.get(`whatsapp/handovers/${handoverId}/`)
   .then(response=>{if(active){setHandover(response.data);setResolutionNote(response.data.resolution_note||"")}})
   .catch(error=>{if(active)setHandoverError(error.response?.data?.detail||"Could not open this assistance case.")})
   .finally(()=>{if(active)setHandoverLoading(false)});
  return()=>{active=false};
 },[handoverId]);

 const updateHandover=async action=>{
  setActionBusy(true);
  try{
   const response=await axiosInstance.patch(`whatsapp/handovers/${handover.id}/`,{
    action,
    resolution_note:resolutionNote,
   });
   setHandover(response.data);
   setResolutionNote(response.data.resolution_note||"");
   toast.success(
    action==="ASSIGN_TO_ME"
     ?"Case assigned to you."
     :action==="UNLOCK_VERIFICATION"
      ?"WhatsApp verification unlocked. The customer can sign in again immediately."
      :`Case ${response.data.status.toLowerCase()}.`
   );
  }catch(error){
   toast.error(error.response?.data?.detail||"Could not update the case.");
  }finally{
   setActionBusy(false);
  }
 };

 const open=async notification=>{
  if(!notification.is_read){
   try{
    await markNotificationRead(notification.id);
   }catch(error){
    console.error("Could not mark notification as read.",error);
   }
  }

  // Opening a message only opens the message. It never logs out or
  // automatically redirects to another workspace.
  setSelected({...notification,is_read:true});
  setData(current=>({
   ...current,
   unread_count:Math.max(
    0,
    current.unread_count-(notification.is_read?0:1)
   ),
   notifications:current.notifications.map(item=>
    item.id===notification.id
     ?{...item,is_read:true}
     :item
   ),
  }));
  if(notification.action_url?.startsWith("/notifications?handover=")){
   setSelected(null);
   navigate(notification.action_url);
   return;
  }
  if(notification.action_url?.startsWith("/notifications?service_request=")){
   setSelected(null);
   navigate(notification.action_url);
   return;
  }
 };

 const remove=async notification=>{
  if(!window.confirm("Delete this notification?"))return;
  await deleteNotification(notification.id);
  if(selected?.id===notification.id)setSelected(null);
  toast.success("Notification deleted.");
  load();
 };

 const toggleUnread=async notification=>{
  const action=notification.is_read?"UNREAD":"READ";
  await markNotificationRead(notification.id,action);
  if(selected?.id===notification.id){
   setSelected({...notification,is_read:action==="READ"});
  }
  load();
 };

 if(serviceRequestId)return <WhatsAppServiceRequest id={serviceRequestId}/>;

 if(handoverId){
  return <section className="comm-page wa-case-page">
   <button className="wa-case-back" onClick={()=>navigate("/notifications")}>
    <i className="bi bi-arrow-left"/> Back to notifications
   </button>
   {handoverLoading&&<div className="comm-empty">Checking the Humba records behind this handover...</div>}
   {handoverError&&<div className="wa-case-error"><i className="bi bi-exclamation-triangle"/><div><strong>Case unavailable</strong><p>{handoverError}</p></div></div>}
   {handover&&<>
    <header className="wa-case-hero">
     <div>
      <span>WHATSAPP ASSISTANCE CASE</span>
      <h1>{handover.case_number}</h1>
      <p>{handover.phone} · Opened {new Date(handover.created_at).toLocaleString()}</p>
     </div>
     <div className={`wa-status wa-status--${handover.status.toLowerCase()}`}>{handover.status}</div>
    </header>

    <div className="wa-case-grid">
     <main>
      <section className="wa-card wa-diagnosis">
       <div className="wa-card-title"><i className="bi bi-activity"/><div><span>SYSTEM DIAGNOSIS</span><h2>{handover.reason_label}</h2></div></div>
       <p className="wa-summary">{handover.summary}</p>
       <div className="wa-attempt"><span>Customer was attempting</span><strong>{handover.attempted_action}</strong></div>
      </section>

      <section className="wa-card">
       <div className="wa-card-title"><i className="bi bi-database-check"/><div><span>DATABASE EVIDENCE</span><h2>What Humba verified</h2></div></div>
       <div className="wa-evidence">
        {Object.entries(handover.evidence||{}).map(([key,value])=><div key={key}>
         <span>{key.replaceAll("_"," ")}</span>
         <strong>{Array.isArray(value)?(value.join(", ")||"None"):typeof value==="boolean"?(value?"Yes":"No"):String(value??"Not recorded")}</strong>
        </div>)}
       </div>
       <div className="wa-security-note"><i className="bi bi-shield-lock"/> The entered National ID and all passwords are excluded. Credential messages remain redacted.</div>
      </section>

      <section className="wa-card">
       <div className="wa-card-title"><i className="bi bi-clock-history"/><div><span>SAFE TIMELINE</span><h2>What the customer was doing</h2></div></div>
       <div className="wa-timeline">
        {(handover.timeline||[]).map((item,index)=><div key={`${item.created_at}-${index}`} className={item.direction.toLowerCase()}>
         <i className={`bi ${item.direction==="INBOUND"?"bi-person":"bi-building"}`}/>
         <div><span>{item.direction==="INBOUND"?"Customer":"Humba assistant"} · {new Date(item.created_at).toLocaleTimeString()}</span><p>{item.body||item.message_type}</p></div>
        </div>)}
       </div>
      </section>
     </main>

     <aside>
      <section className="wa-card wa-next-step">
       <div className="wa-card-title"><i className="bi bi-signpost-split"/><div><span>RECOMMENDED ACTION</span><h2>Resolve safely</h2></div></div>
       <p>{handover.recommended_action}</p>
      </section>

      <section className="wa-card">
       <div className="wa-card-title"><i className="bi bi-shield-check"/><div><span>LINKED RECORDS</span><h2>Policies to review</h2></div></div>
       <div className="wa-policies">
        {(handover.policies||[]).length?handover.policies.map(policy=><button key={policy.id} onClick={()=>navigate(`/policies?policy=${policy.id}`)}>
         <strong>{policy.code}</strong><span>{policy.policyholder||"Policyholder not recorded"}</span><small>{policy.status} · {policy.branch||"No branch"}</small>
        </button>):<p>No policy was safely linked to this case.</p>}
       </div>
      </section>

            <section className="wa-card">
       <div className="wa-card-title"><i className="bi bi-shield-lock"/><div><span>VERIFICATION CONTROL</span><h2>{handover.verification?.locked?"Temporarily locked":"Available"}</h2></div></div>
       <p>
        Failed attempts: <strong>{handover.verification?.failed_attempts||0}</strong>
        {handover.verification?.locked&&handover.verification?.remaining_minutes>0
         ?` · Automatic recovery in about ${handover.verification.remaining_minutes} minute(s)`
         :""}
       </p>
       {handover.verification?.locked&&handover.can_unlock_verification&&<>
        <p>System Administrator override. Confirm the client record first, then enter a resolution note below.</p>
        <button
         disabled={actionBusy}
         onClick={()=>{
          if(!resolutionNote.trim()){
           toast.error("Add a resolution note before unlocking verification.");
           return;
          }
          if(window.confirm("Unlock WhatsApp verification for this customer now?")){
           updateHandover("UNLOCK_VERIFICATION");
          }
         }}
        >
         <i className="bi bi-unlock"/> Unlock WhatsApp verification
        </button>
       </>}
      </section>

<section className="wa-card wa-resolution">
       <div className="wa-card-title"><i className="bi bi-person-check"/><div><span>CASE CONTROL</span><h2>{handover.assigned_to?`Assigned to ${handover.assigned_to}`:"Unassigned"}</h2></div></div>
       {!["RESOLVED","CLOSED"].includes(handover.status)?<>
        <button disabled={actionBusy} className="secondary" onClick={()=>updateHandover("ASSIGN_TO_ME")}>Assign to me</button>
        <label>Resolution note<textarea value={resolutionNote} onChange={event=>setResolutionNote(event.target.value)} placeholder="What was checked or corrected?"/></label>
        <div><button disabled={actionBusy} onClick={()=>updateHandover("RESOLVE")}>Resolve & release customer</button><button disabled={actionBusy} className="secondary" onClick={()=>updateHandover("CLOSE")}>Close case</button></div>
       </>:<>
        <p><strong>{handover.status} by {handover.resolved_by}</strong></p><p>{handover.resolution_note}</p>
        <button disabled={actionBusy} className="secondary" onClick={()=>updateHandover("REOPEN")}>Reopen case</button>
       </>}
      </section>
     </aside>
    </div>
   </>}
  </section>
 }

 return <section className="comm-page">
  <header className="comm-page-head">
   <div>
    <span>COMMUNICATIONS</span>
    <h1>Notifications</h1>
    <p>Read, keep or delete system updates and messages from Humba administrators.</p>
   </div>
   {data.unread_count>0&&<button onClick={async()=>{await markAllNotificationsRead();load()}}>Mark all as read</button>}
  </header>

  <div className="notification-list">
   {loading?<div className="comm-empty">Loading notifications...</div>:
    data.notifications.length?data.notifications.map(n=>
     <article key={n.id} className={`notification-item notification-item--managed ${n.is_read?"":"unread"}`}>
      <button className="notification-open" onClick={()=>open(n)}>
       <i className={`bi ${icon(n.category)}`}/>
       <div>
        <div><strong>{n.title}</strong><span>{new Date(n.created_at).toLocaleString()}</span></div>
        <p>{n.message}</p>
        <small>{n.source} · {n.category}{n.email_status==="SENT"?" · EMAIL SENT":""}</small>
       </div>
       {!n.is_read&&<em/>}
      </button>
      <div className="notification-actions">
       <button onClick={()=>toggleUnread(n)} title={n.is_read?"Mark unread":"Mark read"}>
        <i className={`bi ${n.is_read?"bi-envelope":"bi-envelope-open"}`}/>
       </button>
       <button className="delete" onClick={()=>remove(n)} title="Delete">
        <i className="bi bi-trash"/>
       </button>
      </div>
     </article>
    ):<div className="comm-empty">You have no notifications.</div>}
  </div>

  {selected&&<div className="notification-modal-backdrop" onClick={()=>setSelected(null)}>
   <section className="notification-modal" onClick={event=>event.stopPropagation()}>
    <header>
     <div><span>{selected.source} · {selected.category}</span><h2>{selected.title}</h2></div>
     <button onClick={()=>setSelected(null)}>×</button>
    </header>
    <div className="notification-modal-body">
     <p>{selected.message}</p>
     <small>{new Date(selected.created_at).toLocaleString()}</small>
    </div>
    <footer>
     <button className="secondary" onClick={()=>remove(selected)}><i className="bi bi-trash"/>Delete</button>
     {selected.action_url&&<button onClick={()=>{setSelected(null);navigate(selected.action_url)}}>Open related page</button>}
     <button onClick={()=>setSelected(null)}>Close</button>
    </footer>
   </section>
  </div>}
 </section>
}
